<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import config from "@/common/config";
import IconCross from "@/components/icons/white/IconWhiteCross.vue";
import IconSearch from "@/components/icons/white/IconWhiteSearch.vue";
import VoiceSearch from "@/components/searchBar/VoiceSearch.vue";
import { Autofocus as vAutofocus } from "@/directives";
import { useModalStore } from "@/stores";

import autofocusEventHandler from "./autofocus-event-handler";

interface Props {
  disabled?: boolean;
  autoFocus?: boolean;
  voiceSearchLanguage?: string;
}

withDefaults(defineProps<Props>(), {
  // eslint-disable-next-line vue/no-boolean-default
  autoFocus: true,
  disabled: false,
  voiceSearchLanguage: "da"
});

const emit = defineEmits<{
  search: [query: string];
  reset: [];
  clickSearchIcon: [];
  autoFocusReset: [];
}>();

const query = defineModel<string>("query", { default: "" });

const modalStore = useModalStore();
const { t } = useI18n();

const hasFocus = ref(false);
const searchField = ref<HTMLInputElement | null>(null);

onMounted(() => {
  autofocusEventHandler.addEventListener("keydown", focusOnTypeHandler);
});

onBeforeUnmount(() => {
  autofocusEventHandler.removeEventListener();
});

const focusOnTypeHandler = (): undefined => {
  if (modalStore.isAnyModalOpen) {
    return;
  }

  searchField.value?.focus();
  query.value = "";
  emit("autoFocusReset");
};

const onReset = () => {
  query.value = "";
  emit("reset");
};

const onVoiceFinal = (transcript: string) => {
  query.value = transcript;
  emit("search", query.value);
};
</script>

<template>
  <form
    role="search"
    class="search-bar-bar container d-flex flex-column flex-lg-row align-items-center"
    :class="{ 'search-bar-bar--focus': hasFocus }"
    @submit.prevent="emit('search', query)"
  >
    <div
      class="search-bar-bar__group search-bar-bar__group--stretches search-bar-bar__group--fit-content-lg"
    >
      <div class="search-bar-bar__input-container d-flex flex-row">
        <div
          v-if="voiceSearchLanguage"
          class="search-bar-bar__icon-container search-bar-bar__icon-mic-container"
        >
          <VoiceSearch
            id="searchbar-mic-icon"
            class="search-bar-bar__icon search-bar-bar__icon-button"
            :voice-language="voiceSearchLanguage"
            @final="onVoiceFinal($event)"
          />
        </div>
        <input
          ref="searchField"
          v-model="query"
          v-autofocus="autoFocus"
          class="search-bar-bar__input flex-grow-1"
          type="search"
          dir="auto"
          :placeholder="t('searchBar.search')"
          :aria-label="
            t('searchBar.label', {
              site: config.site.label
            })
          "
          @input="query = ($event.target as HTMLInputElement).value"
          @focus="hasFocus = true"
          @blur="hasFocus = false"
        />
        <slot name="word-completion" v-bind="{ hasFocus }" />

        <div class="search-bar-bar__icon-container">
          <button
            v-if="query.length > 0"
            type="reset"
            class="search-bar-bar__icon-button"
            :aria-label="t('searchBar.resetLabel')"
            @click.prevent="onReset()"
          >
            <IconCross
              class="search-bar-bar__icon search-bar-bar__icon-reset"
              role="presentation"
            ></IconCross>
          </button>
        </div>
        <div>
          <span class="search-bar-bar__icon-separator"></span>
        </div>
        <div class="search-bar-bar__icon-container">
          <button
            type="submit"
            class="search-bar-bar__icon-button"
            :disabled="disabled"
            :aria-label="t('searchBar.search')"
          >
            <IconSearch
              id="searchbar-search-icon"
              class="search-bar-bar__icon search-bar-bar__icon-search"
              :class="{ 'search-bar-bar__icon--disabled': disabled }"
              role="presentation"
              @click="emit('clickSearchIcon')"
            ></IconSearch>
          </button>
        </div>
      </div>
    </div>

    <slot name="actions" />
  </form>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";
@import "@/abc-dsk/styles/mixins.less";

@state-disabled-color: #f0f0f0;

.search-bar-bar {
  display: inline-block;
  padding: 9.6px;
  border-radius: 2px;
  background-color: rgb(@theme-colors[@grey][10]);
  .raised-shadow();

  &__group {
    width: 100%;

    &--stretches {
      flex-grow: 1;
      align-self: stretch;
    }

    &--fit-content-lg {
      #media-breakpoint-up(lg, {
        width: auto;
      });
    }
  }

  &__input-container {
    position: relative;
    width: 100%;
    height: 48px;
    border: 1px solid rgb(@theme-colors[@grey][50]);
    border-radius: 4px;
    background-color: @colors[@white];
  }

  &--focus .search-bar-bar__input-container {
    /* stylelint-disable declaration-block-no-duplicate-properties -- Browser-specific behavior */
    outline: Highlight solid 2px; // IE
    outline: Highlight auto 2px; // Firefox
    outline: -webkit-focus-ring-color auto 2px; // Webkit
    /* stylelint-enable declaration-block-no-duplicate-properties */
  }

  &__input {
    padding: 8px;
    border: none;
    border-radius: 4px;
    outline: none;
    background-color: transparent;
    color: rgb(@theme-colors[@grey][110]);
    font-size: 18px;
    caret-color: rgb(@theme-colors[@primary][base]);

    @lg-rules: {
      padding: 0 8px 0 4px;
      background-color: @colors[@white];
      font-size: 24px;
    };

    #media-breakpoint-up(lg, @lg-rules);

    &::-webkit-search-decoration,
    &::-webkit-search-cancel-button,
    &::-webkit-search-results-button,
    &::-webkit-search-results-decoration {
      display: none;
    }

    &::placeholder {
      color: rgb(@theme-colors[@grey][50]);
    }
  }

  &__icon-container {
    position: relative;
    text-align: center;
  }

  &__icon-mic-container {
    display: none;

    #media-breakpoint-up(lg, {
      display: block;
    });
  }

  &__icon-separator {
    display: block;
    width: 1px;
    height: 100%;
    margin-right: -2px;
    background: linear-gradient(
      180deg,
      @colors[@white] 0%,
      @colors[@white] 15%,
      rgb(@theme-colors[@grey][50]) 15%,
      rgb(@theme-colors[@grey][50]) 85%,
      @colors[@white] 85%,
      @colors[@white] 100%
    );
  }

  &__icon-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    padding: 12px;
    border: none;
    background: none;
    -webkit-appearance: none;
    appearance: none;
  }

  &__icon {
    color: rgb(@theme-colors[@primary][base]);

    &--disabled {
      color: @state-disabled-color;
    }
  }

  &__icon-search {
    width: 21px;
    height: 21px;
  }

  &__icon-reset {
    color: rgb(@theme-colors[@grey][50]);

    [data-theme="colorblind"] & {
      color: rgb(@theme-colors[@primary][base]);
    }
  }
}
</style>
